import { Quaternion, Vector3 } from "three";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelJoint } from "../loader/SugarModelJoint";
import { logger } from "./Logger";
import {
  CONFIGURATOR_DATA,
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
} from "../configurator/ConfiguratorData";

export const fetchAllJoints = (models: SugarModel[]) => {
  return models
    .map((m) => m.joints)
    .reduce((prev, current) => {
      prev = [...prev!, ...(current ?? [])];
      return prev;
    }, []);
};

export const fetchMinDistance = (
  checkModel: SugarModel,
  otherModels: SugarModel[]
): { distance: number; base: any; tail: any } | undefined => {
  const newJoints = checkModel.joints?.filter((j) => !j.binded);
  if (!newJoints) return undefined;

  const outTypes = newJoints.map((n) => n.outType);
  let otherJoints = fetchAllJoints(otherModels.filter((o) => o != checkModel));
  otherJoints = otherJoints?.filter((oj) => outTypes.includes(oj.inType));
  otherJoints = otherJoints?.filter((oj) => !oj.binded);

  const result = { distance: Infinity, base: {}, tail: {} };
  const prevPos = new Vector3();
  const currentPos = new Vector3();

  let found = false;
  for (let i = 0; i < newJoints!.length; i++) {
    newJoints![i].getWorldPosition(currentPos);

    for (let j = 0; j < otherJoints!.length; j++) {
      otherJoints![j].getWorldPosition(prevPos);
      if (newJoints![i].outType !== otherJoints![j].inType) continue;
      if (!checkExcludes(newJoints[i].model, otherJoints![j].model)) continue;

      const distance = prevPos.distanceTo(currentPos);
      if (distance < result.distance) {
        result.distance = distance;
        result.base = otherJoints![j];
        result.tail = newJoints![i];
        found = true;
      }
    }
  }

  return found ? result : undefined;
};

export const fetchScreenSpaceMinDistance = (
  checkModel: SugarModel,
  otherModels: SugarModel[]
): { distance: number; base: any; tail: any } | undefined => {
  const newJoints = checkModel.joints;
  if (!newJoints) return undefined;

  const outTypes = newJoints.map((n) => n.outType);
  let otherJoints = fetchAllJoints(otherModels.filter((o) => o != checkModel));
  otherJoints = otherJoints?.filter((oj) => outTypes.includes(oj.inType));
  otherJoints = otherJoints?.filter((oj) => !oj.binded);

  const result = { distance: Infinity, base: {}, tail: {} };
  const prevPos = new Vector3();
  const currentPos = new Vector3();

  let found = false;
  for (let i = 0; i < newJoints!.length; i++) {
    newJoints![i].getWorldPosition(currentPos);

    for (let j = 0; j < otherJoints!.length; j++) {
      otherJoints![j].getWorldPosition(prevPos);
      if (newJoints![i].outType !== otherJoints![j].inType) continue;
      if (!checkExcludes(newJoints[i].model, otherJoints![i].model)) continue;
      const distance = prevPos.distanceTo(currentPos);
      if (distance < result.distance) {
        result.distance = distance;
        result.base = otherJoints![j];
        result.tail = newJoints![i];
        found = true;
      }
    }
  }

  return found ? result : undefined;
};

export const alignJoints = (
  base: SugarModelJoint,
  tail: SugarModelJoint,
  worldLocal = true
) => {
  if (base == null || tail == null) {
    logger.warn("[JointUtil.alignJoints] base veya tail yok", {
      hasBase: base != null,
      hasTail: tail != null,
    });
    return;
  }

  const baseDirVec = base.direction;
  const tailDirVec = tail.direction;
  if (!baseDirVec || !tailDirVec) {
    logger.warn("[JointUtil.alignJoints] direction tanımlı değil (joint.apply çağrılmamış olabilir)", {
      baseInType: base.inType,
      baseOutType: base.outType,
      tailInType: tail.inType,
      tailOutType: tail.outType,
      hasBaseDirection: !!baseDirVec,
      hasTailDirection: !!tailDirVec,
    });
    return;
  }

  const baseDirection = baseDirVec.clone();
  if (worldLocal) base.getWorldDirection(baseDirection);

  const tailDirection = tailDirVec.clone();
  if (worldLocal) tail.getWorldDirection(tailDirection);

  tailDirection.multiplyScalar(-1);

  const worldRotationBetweenDirs = new Quaternion();
  worldRotationBetweenDirs.setFromUnitVectors(tailDirection, baseDirection);

  tail.getModel()?.applyQuaternion(worldRotationBetweenDirs);

  //posisition shoul be fixed after the rotation
  const tailPosition = tail.offset.clone();
  if (worldLocal) tail.getWorldPosition(tailPosition);
  if (!worldLocal) tailPosition.applyQuaternion(worldRotationBetweenDirs);

  const basePosition = base.offset.clone();
  if (worldLocal) base.getWorldPosition(basePosition);

  tail.getModel()?.position.add(basePosition.sub(tailPosition));
  //
};

export const realignJoints = (
  model: SugarModel,
  ...excludeBindedJoints: SugarModelJoint[]
) => {
  const bindedJoints = model.joints?.filter((j) => j.binded && !j.freeze);
  bindedJoints?.forEach((joint) => {
    alignJoints(joint, joint.bindedJoint!);
    if (!excludeBindedJoints.includes(joint.bindedJoint!)) {
      excludeBindedJoints.push(joint.bindedJoint!);
      realignJoints(joint.bindedJoint!.model!, ...excludeBindedJoints);
    }
  });
};

export const checkExcludes = (baseModel: SugarModel, tailModel: SugarModel) => {
  
  const baseJointInfo = baseModel?.userData?.info?.jointInfo;
  const tailJointInfo = tailModel?.userData?.info?.jointInfo;

  if (!baseJointInfo || !tailJointInfo) return true;

  const baseId = baseJointInfo.id;
  const tailId = tailJointInfo.id;
  const baseExcludes: any[] = baseJointInfo.excludes ?? [];
  const tailExcludes: any[] = tailJointInfo.excludes ?? [];

  return !baseExcludes.includes(tailId) && !tailExcludes.includes(baseId);
};

export const getPossibleBindableJoints = (model: SugarModel) => {
  const bindedJoints = model.joints?.filter((j) => j.binded && !j.freeze) || [];
  return (
    bindedJoints.flatMap((leftJ) => {
      return bindedJoints
        .filter(
          (r) =>
            r.bindedJoint &&
            r.bindedJoint != leftJ &&
            r.bindedJoint?.model != leftJ.model &&
            r.bindedJoint?.inType === leftJ.outType &&
            (r.bindedJoint?.inType === "sol" || r.bindedJoint?.inType === "sag")
        )
        .map((r) => {
          return r.bindedJoint!;
        });
    }) || []
  );
};

export const getJointDataFromModelData = (
  partOption: MODEL_PART_TYPE,
  baseModelData: any,
  configData?: CONFIGURATOR_MODEL_TYPE,
): { tailModelData: any; baseJoint: any } => {
  const models = (configData ?? CONFIGURATOR_DATA).models;
  return baseModelData.jointInfo.modelJoints
    .filter((joint: any) => joint.outType === partOption.type)
    .map((joint: any) => {
      const tailModelData = models.find(
        (m) => m.id == getPartTypeMatch(partOption, baseModelData.id)
      );
      return {
        tailModelData: tailModelData,
        baseJoint: joint,
      };
    });
};

export const getPartTypeMatch = (part: MODEL_PART_TYPE, id: number) => {
  if (part.match)
    return typeof part.match === "number" ? part.match : part.match[id];
  if (part.joints) {
    const p = part.joints.find((jj) =>
      typeof jj.match === "number" ? jj.match : jj.match[id]
    );

    return typeof p?.match === "number" ? p.match : p?.match[id];
  }
};
